import React from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useDispatch } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';

import Button from 'components/Button';
import Message from 'components/Message';
import { closeModal } from 'components/Modal';
import { ICONS } from 'constants/icons';
import appMessages from 'features/App/App.messages';
import { bem } from 'helpers/class-name';

import './ServiceInReviewModal.scss';

const { block, element } = bem('service-in-review-modal');

const messages = defineMessages({
  title: {
    id: 'manage-services.service-in-review-modal.title',
    defaultMessage: 'Your service is in review',
  },
  serviceDisplayName: {
    id: 'manage-services.service-in-review-modal.service-display-name',
    defaultMessage: '{duration} minutes {serviceName}',
  },
  pendingReview: {
    id: 'manage-services.service-in-review-modal.pending-review',
    defaultMessage: 'Pending review',
  },
  whatToExpect: {
    id: 'manage-services.service-in-review-modal.what-to-expect',
    defaultMessage: "Here's what to expect:",
  },
  teamReachOut: {
    id: 'manage-services.service-in-review-modal.team-reach-out',
    defaultMessage: '<strong>Our team will reach out via email</strong> to get more information on your pricing.',
  },
  teamReview: {
    id: 'manage-services.service-in-review-modal.team-review',
    defaultMessage: '<strong>Our team will then review your service</strong> to ensure your pricing is correct.',
  },
  customersCannotSee: {
    id: 'manage-services.service-in-review-modal.customers-cannot-see',
    defaultMessage: "<strong>Customers can't see your service</strong> while it's in review.",
  },
  reviewProcess: {
    id: 'manage-services.service-in-review-modal.review-process',
    defaultMessage:
      '<strong>Your new price</strong> will typically be live in 4–6 business days, once our review is complete.',
  },
  close: {
    id: 'manage-services.service-in-review-modal.close',
    defaultMessage: 'Close',
  },
});

const handleStrong = {
  strong: chunks => <strong>{chunks}</strong>,
};

function ServiceInReviewModal({ serviceName, duration, onClose, payoutConfirmationMessage }) {
  const intl = useIntl();
  const dispatch = useDispatch();

  const closeAriaLabel = intl.formatMessage(appMessages.close);

  function handleClose() {
    if (onClose) {
      onClose();
    } else {
      dispatch(closeModal());
    }
  }

  return (
    <div className={block()}>
      <header className={element('header')}>
        <Message className={element('heading')} as="h2" message={messages.title} />
        <button className={element('close-button')} type="button" onClick={handleClose}>
          <Isvg src={ICONS.cross} aria-label={closeAriaLabel} />
        </button>
      </header>

      <section className={element('service-card')}>
        <div className={element('service-info')}>
          <span className={element('service-name')}>
            <Message message={messages.serviceDisplayName} values={{ duration, serviceName }} />
          </span>
          <div className={element('status-badge')}>
            <Isvg src={ICONS.clock} className={element('status-icon')} />
            <Message message={messages.pendingReview} />
          </div>
        </div>
      </section>

      {payoutConfirmationMessage && (
        <section className={element('payout-confirmation')}>
          <Message as="p" message={payoutConfirmationMessage} />
        </section>
      )}

      <section className={element('content')}>
        <Message as="h3" className={element('section-title')} message={messages.whatToExpect} />

        <div className={element('expectation-list')}>
          <div className={element('expectation-item')}>
            <Isvg src={ICONS.envelope} className={element('expectation-icon')} />
            <div className={element('expectation-text')}>
              <Message message={messages.teamReachOut} values={handleStrong} />
            </div>
          </div>

          <div className={element('expectation-item')}>
            <Isvg src={ICONS.searchGlass} className={element('expectation-icon')} />
            <div className={element('expectation-text')}>
              <Message message={messages.teamReview} values={handleStrong} />
            </div>
          </div>

          <div className={element('expectation-item')}>
            <Isvg src={ICONS.eyeWithCrossDark} className={element('expectation-icon')} />
            <div className={element('expectation-text')}>
              <Message message={messages.customersCannotSee} values={handleStrong} />
            </div>
          </div>

          <div className={element('expectation-item')}>
            <Isvg src={ICONS.clock} className={element('expectation-icon')} />
            <div className={element('expectation-text')}>
              <Message message={messages.reviewProcess} values={handleStrong} />
            </div>
          </div>
        </div>
      </section>

      <footer className={element('footer')}>
        <Message as={Button} className={element('close-btn')} message={messages.close} onClick={handleClose} />
      </footer>
    </div>
  );
}

ServiceInReviewModal.propTypes = {
  serviceName: PropTypes.string.isRequired,
  duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func,
  payoutConfirmationMessage: PropTypes.shape({ id: PropTypes.string }),
};

export default ServiceInReviewModal;
